import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProxmoxService {
  private apiUrl = environment.proxmoxUrl;
  private ticket: string | null = null;
  private csrfToken: string | null = null;
  private apiToken: string | null = null;
  
  // BehaviorSubject to track authentication status
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(private http: HttpClient) { }

  /**
   * Set API token for authentication
   * @param token The API token (e.g., 'PVEAPIToken=root@pam!cloud=fc2bc35d-1107-4887-be1b-8617c970c518')
   */
  setApiToken(token: string): void {
    this.apiToken = token;
    this.isAuthenticatedSubject.next(true);
  }

  /**
   * Login to Proxmox API
   * @param username The username (e.g., 'cloud@pve')
   * @param password The password (e.g., '@<0w6@.>t*:y?wA')
   * @returns Observable with login response
   */
  login(username: string, password: string): Observable<any> {
    // If API token is set, use it for authentication
    if (this.apiToken) {
      this.isAuthenticatedSubject.next(true);
      return of({ data: { ticket: 'api-token-auth', CSRFPreventionToken: 'api-token' } });
    }

    // Prepare login data as form URL encoded
    const body = new URLSearchParams();
    body.set('username', username);
    body.set('password', password);
    
    // Convert to string for the request
    const bodyString = body.toString();
    
    // Set proper headers for form URL encoded data
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json'
    });

    // Log the request for debugging
    console.log('Attempting to login to Proxmox API at:', `${this.apiUrl}/access/ticket`);
    console.log('Request headers:', JSON.stringify(headers.keys().map(key => ({ key, value: headers.get(key) }))));
    console.log('Request body:', bodyString);

    // Make request to proxy server
    return this.http.post(`${this.apiUrl}/access/ticket`, bodyString, { 
      headers: headers,
      withCredentials: false  // Try without credentials first
    })
      .pipe(
        tap((response: any) => {
          // Log the response for debugging
          console.log('Proxmox API response:', response);
          
          // According to Proxmox API, the response should have a data object with ticket and CSRFPreventionToken
          if (response && response.data) {
            this.ticket = response.data.ticket;
            this.csrfToken = response.data.CSRFPreventionToken;
            this.isAuthenticatedSubject.next(true);
          } else {
            throw new Error('Invalid response from Proxmox server: ' + JSON.stringify(response));
          }
        }),
        catchError((error) => {
          console.log('First attempt failed, trying with credentials...');
          // If the first attempt fails, try with credentials
          return this.http.post(`${this.apiUrl}/access/ticket`, bodyString, { 
            headers: headers,
            withCredentials: true
          }).pipe(
            tap((response: any) => {
              console.log('Proxmox API response (with credentials):', response);
              
              if (response && response.data) {
                this.ticket = response.data.ticket;
                this.csrfToken = response.data.CSRFPreventionToken;
                this.isAuthenticatedSubject.next(true);
              } else {
                throw new Error('Invalid response from Proxmox server: ' + JSON.stringify(response));
              }
            }),
            catchError(this.handleError)
          );
        })
      );
  }

  /**
   * Logout from Proxmox API
   */
  logout(): void {
    // If we have a ticket, we should call the logout endpoint
    if (this.ticket && this.csrfToken) {
      const headers = new HttpHeaders({
        'CSRFPreventionToken': this.csrfToken
      });
      
      // Call the logout endpoint
      this.http.post(`${this.apiUrl}/access/ticket`, {}, { headers }).subscribe({
        next: () => {
          // Clear authentication data after successful logout
          this.ticket = null;
          this.csrfToken = null;
          this.apiToken = null;
          this.isAuthenticatedSubject.next(false);
        },
        error: () => {
          // Even if logout fails, clear local authentication data
          this.ticket = null;
          this.csrfToken = null;
          this.apiToken = null;
          this.isAuthenticatedSubject.next(false);
        }
      });
    } else {
      // If no ticket, just clear local data
      this.ticket = null;
      this.csrfToken = null;
      this.apiToken = null;
      this.isAuthenticatedSubject.next(false);
    }
  }

  /**
   * Get authentication status
   */
  isAuthenticated(): boolean {
    return (this.ticket !== null && this.ticket !== '') || (this.apiToken !== null && this.apiToken !== '');
  }

  /**
   * Get CSRF token for API requests
   */
  getCsrfToken(): string | null {
    if (this.apiToken) {
      return 'api-token';
    }
    return this.csrfToken;
  }

  /**
   * Get ticket for API requests
   */
  getTicket(): string | null {
    if (this.apiToken) {
      return 'api-token-auth';
    }
    return this.ticket;
  }

  /**
   * Get headers for API requests
   */
  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    
    if (this.apiToken) {
      // Use API token authentication
      headers = headers.set('Authorization', this.apiToken);
      console.log('Using API token authentication');
    } else if (this.ticket) {
      // Use ticket authentication
      headers = headers.set('Cookie', `PVEAuthCookie=${this.ticket}`);
      if (this.csrfToken) {
        headers = headers.set('CSRFPreventionToken', this.csrfToken);
      }
      console.log('Using ticket authentication');
    } else {
      // Set basic headers for unauthenticated requests
      headers = headers.set('Accept', 'application/json');
      console.log('Using basic authentication');
    }
    
    return headers;
  }

  /**
   * Get cluster status
   */
  getClusterStatus(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching cluster status from Proxmox API at:', `${this.apiUrl}/cluster/status`);
    
    return this.http.get(`${this.apiUrl}/cluster/status`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get nodes list
   */
  getNodes(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching nodes from Proxmox API at:', `${this.apiUrl}/nodes`);
    
    // Make request to proxy server
    return this.http.get(`${this.apiUrl}/nodes`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get node status
   * @param node The node name
   */
  getNodeStatus(node: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching node status from Proxmox API at:', `${this.apiUrl}/nodes/${node}/status`);
    
    return this.http.get(`${this.apiUrl}/nodes/${node}/status`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get node services
   * @param node The node name
   */
  getNodeServices(node: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching node services from Proxmox API at:', `${this.apiUrl}/nodes/${node}/services`);
    
    return this.http.get(`${this.apiUrl}/nodes/${node}/services`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Start a node service
   * @param node The node name
   * @param service The service name
   */
  startNodeService(node: string, service: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Starting node service via Proxmox API at:', `${this.apiUrl}/nodes/${node}/services/${service}/start`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/services/${service}/start`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Stop a node service
   * @param node The node name
   * @param service The service name
   */
  stopNodeService(node: string, service: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Stopping node service via Proxmox API at:', `${this.apiUrl}/nodes/${node}/services/${service}/stop`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/services/${service}/stop`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Restart a node service
   * @param node The node name
   * @param service The service name
   */
  restartNodeService(node: string, service: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Restarting node service via Proxmox API at:', `${this.apiUrl}/nodes/${node}/services/${service}/restart`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/services/${service}/restart`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get VMs list
   */
  getVMs(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching VMs from Proxmox API at:', `${this.apiUrl}/cluster/resources?type=vm`);
    
    // Make request to proxy server
    return this.http.get(`${this.apiUrl}/cluster/resources?type=vm`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get VM status
   * @param node The node name
   * @param type The VM type (qemu or lxc)
   * @param vmid The VM ID
   */
  getVMStatus(node: string, type: string, vmid: number): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching VM status from Proxmox API at:', `${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/current`);
    
    return this.http.get(`${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/current`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get VM configuration
   * @param node The node name
   * @param type The VM type (qemu or lxc)
   * @param vmid The VM ID
   */
  getVMConfig(node: string, type: string, vmid: number): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching VM config from Proxmox API at:', `${this.apiUrl}/nodes/${node}/${type}/${vmid}/config`);
    
    return this.http.get(`${this.apiUrl}/nodes/${node}/${type}/${vmid}/config`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Start a VM
   * @param node The node name
   * @param type The VM type (qemu or lxc)
   * @param vmid The VM ID
   */
  startVM(node: string, type: string, vmid: number): Observable<any> {
    const headers = this.getHeaders();
    console.log('Starting VM via Proxmox API at:', `${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/start`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/start`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Stop a VM
   * @param node The node name
   * @param type The VM type (qemu or lxc)
   * @param vmid The VM ID
   */
  stopVM(node: string, type: string, vmid: number): Observable<any> {
    const headers = this.getHeaders();
    console.log('Stopping VM via Proxmox API at:', `${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/stop`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/stop`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Reset a VM
   * @param node The node name
   * @param type The VM type (qemu or lxc)
   * @param vmid The VM ID
   */
  resetVM(node: string, type: string, vmid: number): Observable<any> {
    const headers = this.getHeaders();
    console.log('Resetting VM via Proxmox API at:', `${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/reset`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/reset`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Shutdown a VM
   * @param node The node name
   * @param type The VM type (qemu or lxc)
   * @param vmid The VM ID
   */
  shutdownVM(node: string, type: string, vmid: number): Observable<any> {
    const headers = this.getHeaders();
    console.log('Shutting down VM via Proxmox API at:', `${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/shutdown`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/shutdown`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Suspend a VM
   * @param node The node name
   * @param type The VM type (qemu or lxc)
   * @param vmid The VM ID
   */
  suspendVM(node: string, type: string, vmid: number): Observable<any> {
    const headers = this.getHeaders();
    console.log('Suspending VM via Proxmox API at:', `${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/suspend`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/suspend`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Resume a VM
   * @param node The node name
   * @param type The VM type (qemu or lxc)
   * @param vmid The VM ID
   */
  resumeVM(node: string, type: string, vmid: number): Observable<any> {
    const headers = this.getHeaders();
    console.log('Resuming VM via Proxmox API at:', `${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/resume`);
    
    return this.http.post(`${this.apiUrl}/nodes/${node}/${type}/${vmid}/status/resume`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get storage list
   */
  getStorage(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching storage from Proxmox API at:', `${this.apiUrl}/storage`);
    
    return this.http.get(`${this.apiUrl}/storage`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get storage status
   * @param storage The storage name
   */
  getStorageStatus(storage: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching storage status from Proxmox API at:', `${this.apiUrl}/storage/${storage}/status`);
    
    return this.http.get(`${this.apiUrl}/storage/${storage}/status`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get storage content
   * @param storage The storage name
   * @param content The content type (optional)
   */
  getStorageContent(storage: string, content?: string): Observable<any> {
    const headers = this.getHeaders();
    let url = `${this.apiUrl}/storage/${storage}/content`;
    if (content) {
      url += `?content=${content}`;
    }
    console.log('Fetching storage content from Proxmox API at:', url);
    
    return this.http.get(url, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Create storage content (e.g., VM images)
   * @param storage The storage name
   * @param params The parameters for creating content
   */
  createStorageContent(storage: string, params: any): Observable<any> {
    const headers = this.getHeaders();
    console.log('Creating storage content via Proxmox API at:', `${this.apiUrl}/storage/${storage}/content`);
    
    return this.http.post(`${this.apiUrl}/storage/${storage}/content`, params, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get access control information
   */
  getAccessControl(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching access control from Proxmox API at:', `${this.apiUrl}/access`);
    
    return this.http.get(`${this.apiUrl}/access`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get users list
   */
  getUsers(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching users from Proxmox API at:', `${this.apiUrl}/access/users`);
    
    return this.http.get(`${this.apiUrl}/access/users`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get user information
   * @param userid The user ID
   */
  getUser(userid: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching user information from Proxmox API at:', `${this.apiUrl}/access/users/${userid}`);
    
    return this.http.get(`${this.apiUrl}/access/users/${userid}`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Create a new user
   * @param userid The user ID
   * @param params The user parameters
   */
  createUser(userid: string, params: any): Observable<any> {
    const headers = this.getHeaders();
    console.log('Creating user via Proxmox API at:', `${this.apiUrl}/access/users`);
    
    // Add userid to params
    const body = new URLSearchParams();
    body.set('userid', userid);
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        body.set(key, params[key]);
      }
    }
    
    return this.http.post(`${this.apiUrl}/access/users`, body.toString(), { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Update user information
   * @param userid The user ID
   * @param params The user parameters
   */
  updateUser(userid: string, params: any): Observable<any> {
    const headers = this.getHeaders();
    console.log('Updating user via Proxmox API at:', `${this.apiUrl}/access/users/${userid}`);
    
    // Convert params to URLSearchParams
    const body = new URLSearchParams();
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        body.set(key, params[key]);
      }
    }
    
    return this.http.put(`${this.apiUrl}/access/users/${userid}`, body.toString(), { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Delete a user
   * @param userid The user ID
   */
  deleteUser(userid: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Deleting user via Proxmox API at:', `${this.apiUrl}/access/users/${userid}`);
    
    return this.http.delete(`${this.apiUrl}/access/users/${userid}`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Change user password
   * @param userid The user ID
   * @param password The new password
   */
  changeUserPassword(userid: string, password: string): Observable<any> {
    const headers = this.getHeaders();
    console.log('Changing user password via Proxmox API at:', `${this.apiUrl}/access/users/${userid}/password`);
    
    const body = new URLSearchParams();
    body.set('password', password);
    
    return this.http.put(`${this.apiUrl}/access/users/${userid}/password`, body.toString(), { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get groups list
   */
  getGroups(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching groups from Proxmox API at:', `${this.apiUrl}/access/groups`);
    
    return this.http.get(`${this.apiUrl}/access/groups`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get roles list
   */
  getRoles(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching roles from Proxmox API at:', `${this.apiUrl}/access/roles`);
    
    return this.http.get(`${this.apiUrl}/access/roles`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get permissions
   * @param userid The user ID (optional)
   * @param path The path (optional)
   */
  getPermissions(userid?: string, path?: string): Observable<any> {
    const headers = this.getHeaders();
    let url = `${this.apiUrl}/access/permissions`;
    
    // Add query parameters if provided
    const params = new URLSearchParams();
    if (userid) params.set('userid', userid);
    if (path) params.set('path', path);
    
    if (params.toString()) {
      url += '?' + params.toString();
    }
    
    console.log('Fetching permissions from Proxmox API at:', url);
    
    return this.http.get(url, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Get pools list
   */
  getPools(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Fetching pools from Proxmox API at:', `${this.apiUrl}/pools`);
    
    return this.http.get(`${this.apiUrl}/pools`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Test connection to Proxmox API with current authentication
   */
  testAuthenticatedConnection(): Observable<any> {
    const headers = this.getHeaders();
    console.log('Testing authenticated connection to Proxmox API at:', `${this.apiUrl}/cluster/status`);
    
    // Try to get cluster status as a simple test
    return this.http.get(`${this.apiUrl}/cluster/status`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Test connection to Proxmox API
   */
  testConnection(): Observable<any> {
    console.log('Testing connection to Proxmox API at:', `${this.apiUrl}/access`);
    
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });
    
    // Make request to proxy server
    return this.http.get(`${this.apiUrl}/access`, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred!';
    console.log('Handling error:', error);
    
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      // Server-side errors
      if (error.status === 0) {
        // Network error (could be CORS or connectivity issue)
        errorMessage = 'Network error: Unable to connect to Proxmox server. This typically happens when:\n' +
                      '1. The backend proxy server is not running\n' +
                      '2. There are network/firewall restrictions\n' +
                      '3. The Proxmox server is not accessible\n\n' +
                      'Detailed error information:\n' +
                      `- Error name: ${error.name}\n` +
                      `- Error message: ${error.message}\n`;
      } else if (error.status === 401) {
        errorMessage = 'Authentication failed. Please check your credentials.';
      } else if (error.status === 403) {
        errorMessage = 'Access denied. You do not have permission to perform this action.';
      } else if (error.status === 404) {
        errorMessage = 'API endpoint not found. Please check the Proxmox server configuration and ensure it is running.';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else {
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      }
    }
    console.error('Proxmox API Error:', errorMessage);
    return throwError(() => errorMessage);
  }
}